import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { api } from '../services/api';
import QueueCards from '../components/QueueCards';

// Admin v2 · V8 — the founder landing page, per the approved mockup.
//
// Two layers with different physics: the METRICS (tiles + funnel strip) are
// windowed by the period selector; the DO-NOW queues are live state and
// ignore it. The funnel strip carries its own Campaign/Period scope toggle —
// one-day funnels are too thin to read. Killed relative to the old
// Dashboard: At Risk, assessment Activation, Stickiness, Time-to-Value,
// Streak Distribution, Quick Stats — every number here states its basis.

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This wk' },
  { value: 'last_week', label: 'Last wk' },
  { value: '30d', label: '30d' },
];

function Delta({ value, prev }) {
  if (prev === null || prev === undefined || value === prev) return null;
  const up = value > prev;
  return (
    <span className={`text-[13px] font-semibold ${up ? 'text-teacher-green' : 'text-teacher-red'}`}>
      {up ? '▲' : '▼'} {up ? '+' : ''}{value - prev} vs prev
    </span>
  );
}

function Tile({ title, value, delta, sub, valueClass = 'text-teacher-text-primary' }) {
  return (
    <div className="bg-white border border-teacher-border rounded-xl px-4 py-3.5">
      <div className="text-[13px] font-medium text-teacher-text-secondary">{title}</div>
      <div className={`text-3xl font-bold mt-1 ${valueClass}`}>
        {value} {delta}
      </div>
      {sub && <div className="text-xs text-teacher-text-tertiary mt-1">{sub}</div>}
    </div>
  );
}

function FunnelStrip({ funnel }) {
  const [scope, setScope] = useState('campaign');
  const f = scope === 'campaign' ? funnel.campaign : funnel.period;
  const stages = [
    { label: 'signed up', value: f.signups, bg: '#6B46C1', fg: '#FFFFFF' },
    { label: 'opened a session', value: f.opened, bg: '#A855F7', fg: '#FFFFFF' },
    { label: 'answered ≥1', value: f.answered, bg: '#DDD6FE', fg: '#3B2A66' },
  ];
  const top = Math.max(f.signups, 1);
  const drops = [
    { label: 'never opened', n: f.signups - f.opened },
    { label: 'opened, no answer', n: f.opened - f.answered },
  ];
  const biggest = drops.reduce((a, b) => (b.n > a.n ? b : a), drops[0]);

  return (
    <div className="bg-white border border-teacher-border rounded-xl px-5 py-4">
      <div className="flex items-center justify-between mb-3.5">
        <h3 className="text-sm font-semibold text-teacher-text-primary">Signup → first answer</h3>
        <div className="flex items-center bg-teacher-bg rounded-md p-0.5 text-[11px] font-medium text-teacher-text-secondary">
          <button
            onClick={() => setScope('campaign')}
            className={`px-2.5 py-1 rounded ${scope === 'campaign' ? 'bg-white border border-teacher-border font-semibold text-teacher-text-primary' : ''}`}
          >
            Campaign · {format(new Date(`${funnel.campaign.start}T00:00:00`), 'MMM d')} →
          </button>
          <button
            onClick={() => setScope('period')}
            className={`px-2.5 py-1 rounded ${scope === 'period' ? 'bg-white border border-teacher-border font-semibold text-teacher-text-primary' : ''}`}
          >
            Selected period
          </button>
        </div>
      </div>
      <div className="flex gap-2">
        {stages.map((s) => (
          <div
            key={s.label}
            className="rounded-lg px-3.5 py-2.5 min-w-[110px]"
            style={{ width: `${Math.max(14, (s.value / top) * 82)}%`, background: s.bg, color: s.fg }}
          >
            <div className="text-xl font-bold">{s.value}</div>
            <div className="text-xs opacity-85 whitespace-nowrap">{s.label}{s.value > 0 && s.label !== 'signed up' ? ` · ${Math.round((s.value / top) * 100)}%` : ''}</div>
          </div>
        ))}
      </div>
      <div className="flex items-baseline justify-between gap-4 mt-2.5">
        <p className="text-[11px] text-teacher-text-tertiary">
          "opened" = ever created a practice session or quiz — per-session precision arrives with build 153's events
        </p>
        <p className="text-xs text-teacher-text-secondary whitespace-nowrap">
          {biggest.n > 0 && <>biggest leak: <span className="font-semibold text-teacher-red">−{biggest.n} {biggest.label}</span> · </>}
          <Link to="/funnel" className="font-semibold text-primary-700 hover:text-primary-800">drop-off mechanics → Funnel</Link>
        </p>
      </div>
    </div>
  );
}

export default function OverviewPage() {
  const [period, setPeriod] = useState('yesterday');
  const [overview, setOverview] = useState(null);
  const [queues, setQueues] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getOverview(period)
      .then((r) => { if (!cancelled) { setOverview(r.data); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  useEffect(() => {
    api.getQueues().then((r) => setQueues(r.data)).catch(() => {});
  }, []);

  const t = overview?.tiles;
  const wave = queues?.trials_expiring?.wave;
  const failed = queues?.errors?.generateFailed24h;
  const rangeLabel = overview
    ? overview.range.start === overview.range.end
      ? format(new Date(`${overview.range.end}T00:00:00`), 'EEE d MMM')
      : `${format(new Date(`${overview.range.start}T00:00:00`), 'd MMM')} – ${format(new Date(`${overview.range.end}T00:00:00`), 'd MMM')}`
    : '';

  return (
    <div className="p-6 flex flex-col gap-5">
      {/* Header + period selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-teacher-text-primary">Overview</h1>
          <div className="flex items-center bg-white border border-teacher-border rounded-lg p-0.5 text-xs font-medium text-teacher-text-secondary">
            {PERIOD_OPTIONS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 rounded-md transition-colors ${
                  period === p.value ? 'bg-primary-600 text-white font-semibold' : 'hover:text-primary-700'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="text-xs text-teacher-text-tertiary">
          {rangeLabel} · IST
          {overview?.trend?.anchoredTo && ` · metrics through ${format(new Date(`${overview.trend.anchoredTo}T00:00:00`), 'd MMM')}`}
        </div>
      </div>

      {error && <div className="text-sm text-teacher-red">Overview failed to load: {error}</div>}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3.5">
        <Tile
          title="Signups"
          value={loading ? '…' : t?.signups.value ?? '—'}
          delta={!loading && t && <Delta value={t.signups.value} prev={t.signups.prev} />}
          sub={t && `${t.signups.bySource.organic} organic · ${t.signups.bySource.coaching} coaching · ${t.signups.bySource.reel} reel`}
        />
        <Tile
          title="Reached an answer"
          value={loading ? '…' : t ? <>{t.firstAnswers.value}<span className="text-base font-medium text-teacher-text-tertiary"> / {t.firstAnswers.of}</span></> : '—'}
          sub="of the period's signups · answered ≥1"
        />
        <Tile
          title="Active students"
          value={loading ? '…' : t?.actives.value ?? '—'}
          delta={!loading && t && <Delta value={t.actives.value} prev={t.actives.prev} />}
          sub={t && t.actives.latestWau !== null ? `week ${t.actives.latestWau} · month ${t.actives.latestMau}` : undefined}
        />
        <Tile
          title="Trials ending ≤7d"
          value={queues ? queues.trials_expiring.count : '…'}
          valueClass="text-primary-700"
          sub={wave
            ? <span className="font-medium text-teacher-amber">{wave.count} end {format(new Date(`${wave.startDate}T00:00:00+05:30`), 'MMM d')}–{format(new Date(`${wave.endDate}T00:00:00+05:30`), 'MMM d')}</span>
            : 'no wave in 60d'}
        />
        <Tile
          title="Student errors · 24h"
          value={queues ? (failed === null ? '—' : failed) : '…'}
          valueClass={failed ? 'text-teacher-amber' : 'text-teacher-text-primary'}
          sub={<span>failed generations · <a href="https://jeevibe.sentry.io" target="_blank" rel="noopener noreferrer" className="text-primary-700 font-semibold">Sentry</a></span>}
        />
      </div>

      {/* Funnel strip */}
      {overview && <FunnelStrip funnel={overview.funnel} />}

      {/* DO NOW queues — live, not windowed */}
      <QueueCards data={queues} />
    </div>
  );
}
